<template>
  <div :class="ns.b()">
    <header v-if="showHeader" :class="ns.e('header')">
      <div>
        <h2 :class="ns.e('title')">数据源管理</h2>
        <p :class="ns.e('desc')">
          数据源来自
          <strong>SELECT SQL</strong>，特点：<strong>只读查询</strong>。
          对接后端 <code>/system/table-config</code>，通过解析 SQL 获取字段。
          列表使用 <code>ZGrid</code>，勾选后支持批量校验。
        </p>
      </div>
    </header>

    <z-grid :options="gridOptions" :height="height" />

    <section v-if="showDetail && activeSource" :class="ns.e('detail')">
      <h3 :class="ns.e('detail-title')">字段配置 · {{ activeSource.name }}</h3>
      <p :class="ns.e('detail-desc')">
        解析 SQL 得到的字段（{{ activeSource.fields.length }}），已启用
        {{ enabledFieldCount(activeSource) }} 个
      </p>
      <div :class="ns.e('field-chips')">
        <z-tag
          v-for="field in activeSource.fields"
          :key="field.field"
          :type="field.enabled ? 'primary' : 'info'"
          :effect="field.enabled ? 'light' : 'plain'"
        >
          {{ field.label }}
          <span :class="ns.e('field-type')">{{ field.type }}</span>
        </z-tag>
      </div>
    </section>

    <details v-if="showSchema" :class="ns.e('schema')">
      <summary>数据源表结构（system · flag · name · sql）</summary>
      <pre>{{ schemaPreview }}</pre>
    </details>

    <data-source-create-dialog
      v-model="dialogVisible"
      :editing="editingSource"
      :system-options="systemOptions"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import ZGrid, {
  GridOption,
  GRID_HTTP_INJECTION_KEY,
} from '@zoryn/components/grid'
import { ZMessage } from '@zoryn/components/message'
import ZTag from '@zoryn/components/tag'
import { useNamespace } from '@zoryn/hooks'
import type { HttpRequest } from '@zoryn/network'
import { buildDataSourcePath } from './constants'
import { fetchTableConfigDetail } from './data-source-api'
import DataSourceCreateDialog from './data-source-create-dialog.vue'
import { dataSourceEmits, dataSourceProps } from './data-source-component'
import {
  createDataSourceGridOptions,
  resolveValidateStatus,
} from './data-source-grid'
import type { SqlDataSource, SqlDataSourceField } from './types'

defineOptions({
  name: 'ZDataSource',
})

const props = defineProps(dataSourceProps)
const emit = defineEmits(dataSourceEmits)

const ns = useNamespace('data-source')
const request = inject<HttpRequest | null>(GRID_HTTP_INJECTION_KEY, null)

const dialogVisible = ref(false)
const editingSource = ref<SqlDataSource | null>(null)
const activeRow = ref<Record<string, unknown> | null>(null)
const listRows = ref<Record<string, unknown>[]>([])

const gridOptions = request
  ? createDataSourceGridOptions(request, {
      onCreate: openCreate,
      onEdit: openEdit,
      onCurrentRowChange(row) {
        activeRow.value = row
      },
      onAfterQuery(rows) {
        listRows.value = rows
        if (activeRow.value) {
          const currentId = String(activeRow.value.id ?? '')
          const stillExists = rows.some(
            (row) => String(row.id ?? '') === currentId
          )
          if (!stillExists) {
            activeRow.value = rows[0] ?? null
          }
        } else if (rows[0]) {
          activeRow.value = rows[0]
        }
      },
    })
  : new GridOption({
      module: '/system/table-config',
      columns: [{ field: 'dataSourceName', title: '数据源名称' }],
    })

const activeSource = computed(() => {
  const row = activeRow.value
  if (!row) return null
  return rowToSqlDataSource(row)
})

const schemaPreview = computed(() =>
  JSON.stringify(
    listRows.value.map((item) => {
      const fields = (item._fields as SqlDataSourceField[]) ?? []
      return {
        system: item.systemFlag,
        dataSourceFlag: item.dataSourceFlag,
        path: buildDataSourcePath(
          String(item.systemFlag ?? ''),
          String(item.dataSourceFlag ?? '')
        ),
        name: item.dataSourceName,
        sql: item.customSql,
        validateStatus: resolveValidateStatus(item),
        fields: fields.filter((field) => field.enabled),
      }
    }),
    null,
    2
  )
)

function rowToSqlDataSource(row: Record<string, unknown>): SqlDataSource {
  const fields = (row._fields as SqlDataSourceField[]) ?? []
  const status = resolveValidateStatus(row)
  return {
    id: String(row.id ?? ''),
    system: String(row.systemFlag ?? ''),
    dataSourceFlag: String(row.dataSourceFlag ?? ''),
    name: String(row.dataSourceName ?? ''),
    sql: String(row.customSql ?? ''),
    description: typeof row.remark === 'string' ? row.remark : undefined,
    fields,
    validateStatus: status,
    validateMessage:
      typeof row.validateMessage === 'string' ? row.validateMessage : undefined,
    validatedAt:
      typeof row.validateTime === 'string' ? row.validateTime : undefined,
  }
}

function enabledFieldCount(row: SqlDataSource) {
  return row.fields.filter((item) => item.enabled).length
}

function openCreate() {
  editingSource.value = null
  dialogVisible.value = true
}

async function openEdit(row: Record<string, unknown>) {
  if (!request) {
    ZMessage.error('[ZDataSource] 未配置 HttpRequest')
    return
  }
  activeRow.value = row
  try {
    editingSource.value = await fetchTableConfigDetail(request, Number(row.id))
    dialogVisible.value = true
  } catch (err) {
    ZMessage.error(err instanceof Error ? err.message : '加载详情失败')
  }
}

async function handleSaved() {
  emit('saved')
  await gridOptions.queryByExamplePage?.()
}

defineExpose({
  refreshList: () => gridOptions.queryByExamplePage?.(),
  getGridOptions: () => gridOptions,
})
</script>
